"use client";

import Button from "@afa-shared/afa-components/dist/Buttons/Button";
import Heading from "@afa-shared/afa-components/dist/Heading";
import Message from "@components/Message/Message";
import PaginationBlock from "@components/Pagination";
import { isNullOrEmpty } from "@utils/string";
import { parseAsInteger, parseAsString, useQueryState } from "nuqs";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";

import { IFocusState, useFocusState } from "hooks/zustand/useFocusState";
import { EducationProviderItem, EducationSearchItem } from "types/EducationProviderItem";

import EducationAccordion from "./EducationAccordion";
import educationListStyles from "./EducationList.module.css";
import { CourseTypes, filterCourses, filterQuery } from "./EducationListHelper";
import EducationSearch from "./EducationSearch";

interface IEducationList {
  educationProviders: Array<EducationProviderItem>;
  educationSearchList: Array<EducationSearchItem>;
}

const ITEMS_PER_PAGE = 10;

const collatorSv = new Intl.Collator("sv", {
  sensitivity: "base",
  numeric: true,
  ignorePunctuation: true,
});

const safe = (s: unknown) => (typeof s === "string" ? s.trim() : "");

function compareProvider(
  a: { name?: string; organizationalNumber?: string },
  b: { name?: string; organizationalNumber?: string },
) {
  const nA = safe(a?.name);
  const nB = safe(b?.name);

  const primary = collatorSv.compare(nA, nB);
  if (primary !== 0) return primary;

  const kA = safe(a?.organizationalNumber);
  const kB = safe(b?.organizationalNumber);
  return collatorSv.compare(kA, kB);
}

const EducationList = ({ educationProviders, educationSearchList }: IEducationList) => {
  const setOverridePageTopFocus = useFocusState((s: IFocusState) => s.setOverridePageTopFocus);

  const [query, setQuery] = useQueryState("query", parseAsString.withDefault(""));
  const [courseTypes, setCourseTypes] = useQueryState("courseTypes", parseAsString.withDefault(""));
  const [page, setPage] = useQueryState("page", parseAsInteger.withDefault(1));

  const [courseTypeFilterChanged, setCourseTypeFilterChanged] = useState(false);

  const listRef = useRef<HTMLDivElement | null>(null);

  const courseTypesArray = useMemo(
    () => (courseTypes ? courseTypes.split(",").filter(Boolean) : []),
    [courseTypes],
  );
  const isActive = (t: number) => courseTypesArray.includes(String(t));

  const filteredSorted = useMemo(() => {
    const base = isNullOrEmpty(query)
      ? educationProviders
      : filterQuery(query, educationProviders, educationSearchList);

    const typed = isNullOrEmpty(courseTypes) ? base : filterCourses(courseTypes, base);

    const sorted = [...(typed ?? [])].sort(compareProvider);

    return sorted;
  }, [query, courseTypes, educationProviders, educationSearchList]);

  const totalItems = filteredSorted.length;

  const safePage = useMemo(() => {
    const maxPage = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
    const n = Number.isFinite(page) && page > 0 ? page : 1;
    return Math.min(n, maxPage);
  }, [page, totalItems]);

  const pageItems = useMemo(() => {
    const start = ITEMS_PER_PAGE * (safePage - 1);
    const end = start + ITEMS_PER_PAGE;
    return filteredSorted.slice(start, end);
  }, [filteredSorted, safePage]);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage === 1 ? null : safePage, { history: "replace" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safePage]);

  const hasAnyUrlState = useMemo(() => {
    return (
      (!!query && query.length > 0) || (!!courseTypes && courseTypes.length > 0) || (page ?? 1) > 1
    );
  }, [query, courseTypes, page]);

  useEffect(() => {
    if (hasAnyUrlState && !courseTypeFilterChanged) {
      listRef.current?.focus?.();
    }
    if (courseTypeFilterChanged) {
      const id = requestAnimationFrame(() => setCourseTypeFilterChanged(false));
      return () => cancelAnimationFrame(id);
    }
  }, [hasAnyUrlState, courseTypeFilterChanged]);

  const handleSearch = (value: string) => {
    const v = value.trim();
    setQuery(v.length ? v : null);
    setPage(null);

    if (v.length) setOverridePageTopFocus(true);
    else listRef.current?.focus();
  };

  const toggleCourseType = (value: string) => {
    const set = new Set(courseTypesArray);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    const next = Array.from(set).join(",");
    setCourseTypes(next.length ? next : null);
    setPage(null);

    setCourseTypeFilterChanged(true);
    setOverridePageTopFocus(true);
  };

  return (
    <div className={educationListStyles.educationList}>
      <div className={educationListStyles.filterWrapper}>
        <EducationSearch
          educationSearchList={educationSearchList}
          searchQuery={query}
          onSearch={handleSearch}
        />
        <div className={educationListStyles.filterButtonWrapper}>
          <Button
            label="Öppna utbildningar"
            buttonAriaLabel="Filtrera på öppna utbildningar"
            buttonType="secondary"
            margin="0"
            value={CourseTypes.open}
            onClick={() => toggleCourseType(String(CourseTypes.open))}
            className={isActive(CourseTypes.open) ? educationListStyles.isActive : ""}
            aria-pressed={isActive(CourseTypes.open) ? "true" : "false"}
          />
          <Button
            label="Digitala utbildningar"
            buttonAriaLabel="Filtrera på digitala utbildningar"
            buttonType="secondary"
            margin="0"
            value={CourseTypes.digital}
            onClick={() => toggleCourseType(String(CourseTypes.digital))}
            className={isActive(CourseTypes.digital) ? educationListStyles.isActive : ""}
            aria-pressed={isActive(CourseTypes.digital) ? "true" : "false"}
          />
          <Button
            label="Företagsanpassade"
            buttonAriaLabel="Filtrera på företagsanpassade utbildningar"
            margin="0"
            buttonType="secondary"
            value={CourseTypes.business}
            onClick={() => toggleCourseType(String(CourseTypes.business))}
            className={isActive(CourseTypes.business) ? educationListStyles.isActive : ""}
            aria-pressed={isActive(CourseTypes.business) ? "true" : "false"}
          />
          <Button
            label="E-utbildningar"
            buttonAriaLabel="Filtrera på e-utbildningar"
            buttonType="secondary"
            margin="0"
            value={CourseTypes.online}
            onClick={() => toggleCourseType(String(CourseTypes.online))}
            className={isActive(CourseTypes.online) ? educationListStyles.isActive : ""}
            aria-pressed={isActive(CourseTypes.online) ? "true" : "false"}
          />
        </div>
      </div>

      <div
        ref={listRef}
        className={educationListStyles.resultWrapper}
        tabIndex={-1}
        data-temp-keep-index
      >
        <Heading
          className={`${educationListStyles.heading} ${
            pageItems.length > 0 ? educationListStyles.showNoResult : ""
          }`}
          aria-live="polite"
          aria-atomic="true"
          variant="h2"
        >
          <span>{totalItems}</span> träffar
        </Heading>

        {pageItems.length > 0 ? (
          <>
            <EducationAccordion educationProviders={pageItems} />
            {totalItems > 0 && ITEMS_PER_PAGE < totalItems && (
              <Suspense>
                <NuqsAdapter>
                  <PaginationBlock
                    itemsPerPage={ITEMS_PER_PAGE}
                    totalItems={totalItems}
                    searchParameters={{ query, courseTypes, page: safePage }}
                    navbarLabel={"Paginering för träfflista"}
                    className={educationListStyles.pagination}
                    listRef={listRef}
                  />
                </NuqsAdapter>
              </Suspense>
            )}
          </>
        ) : (
          <Message
            type="info"
            heading="Din sökning gav inga träffar"
            headingVariant="h2"
            children={""}
            className={educationListStyles.noResultFeedback}
          />
        )}
      </div>
    </div>
  );
};

export default EducationList;
